<template>
    <div>
        <div class="mb-3">
            <b-row cols="1">
                <b-col>
                    <b-button v-if="!connected" variant="primary" :disabled="starting" @click="start">
                        <i class="fa fa-play mr-2" />Iniciar visualização
                    </b-button>
                </b-col>
                <b-col class="mt-3">
                    <b-alert variant="secondary" :show="true">{{
                        status
                    }}</b-alert>
                </b-col>
            </b-row>
        </div>
        <div style="height: 600px" class="d-flex border rounded">
            <vtk-sidebar v-if="connected" :busy="busy" :vtk-files="vtkFiles" :vtk-items="vtkItems"
                @changeVisibility="changeVisibility" @changeRepresentation="changeRepresentation"
                @changeColorBy="changeColorBy" @resetCamera="resetCamera"
                @revertTransferFunctions="revertTransferFunctions" />
            <vtk-view v-if="rerenderVtkView" ref="vtkView" />
        </div>
    </div>
</template>
<script>
import vtkWSLinkClient from "vtk.js/Sources/IO/Core/WSLinkClient";
import {
    connectImageStream,
    disconnectImageStream
} from "vtk.js/Sources/Rendering/Misc/RemoteView";
import SmartConnect from "wslink/src/SmartConnect";
import sleep from "@/utils/sleep";
import protocol from "../protocol";
import VtkView from "./VtkView";
import VtkSidebar from "./VtkSidebar";

vtkWSLinkClient.setSmartConnectClass(SmartConnect);

export default {
    name: "VtkViewer",
    components: {
        VtkView,
        VtkSidebar
    },
    props: {
        vtkFiles: {
            type: Array,
            default() {
                return [];
            }
        },
        initialColorBy: {
            type: Object,
            default() {
                return {
                    location: "POINTS",
                    name: "Displacement",
                    component: 2,
                    componentName: "Z",
                    vectorMode: "Component"
                };
            }
        }
    },
    data() {
        return {
            starting: false,
            connected: false,
            client: null,
            status: "Pronto para iniciar.",
            busy: true,
            vtkItems: [],
            rerenderVtkView: true
        };
    },
    mounted() {
        window.addEventListener("beforeunload", this.destroyVisualization);
    },
    async beforeDestroy() {
        await this.destroyVisualization();
        window.removeEventListener("beforeunload", this.destroyVisualization);
    },
    methods: {
        forceRerenderVtkView() {
            // Remove my-component from the DOM
            this.rerenderVtkView = false;

            this.$nextTick(() => {
                // Add the component back in
                this.rerenderVtkView = true;
            });
        },
        async destroyVisualization() {
            const { client } = this;
            if (client && client.isConnected()) {
                try {
                    await this.deleteProxies();
                } catch (error) {
                    console.log(error);
                }
                this.disconnect();
            }
            disconnectImageStream();
            this.connected = false;
            this.starting = false;
            this.client = null;
            this.status = "Pronto para iniciar.";
            this.vtkItems = [];
            this.forceRerenderVtkView();
        },
        async start() {
            this.starting = true;
            this.status = "Conectando...";
            await this.$nextTick();
            await sleep(1000);
            this.connect();
        },
        connect() {
            let { client } = this;

            this.disconnect();

            if (!client) {
                client = vtkWSLinkClient.newInstance({
                    createImageStream: false
                });
                client.setProtocols({
                    SwxVisualizer: protocol
                });
            }

            client.onBusyChange(count => {
                this.busy = count > 0;
            });

            client.beginBusy();

            client.onConnectionError(request => {
                const message =
                    (request && request.response && request.response.error) ||
                    "Erro de conexão, tente novamente.";
                console.error(message);
                console.log(request);
                this.destroyVisualization().then(() => {
                    this.status = message;
                });
            });

            client.onConnectionClose(request => {
                const message =
                    (request && request.response && request.response.error) ||
                    "Conexão encerrada, tente novamente.";
                console.error(message);
                console.log(request);
                this.destroyVisualization().then(() => {
                    this.status = message;
                });
            });

            const config = {
                application: "swxvisualizer"
            };

            if (location.hostname.split(".")[0] === "localhost") {
                // config.sessionURL = "ws://localhost:1234/ws";
                config.sessionManagerURL = "http://localhost:9091/paraview";
            }

            client
                .connect(config)
                .then(validClient => {
                    client.endBusy();
                    this.onConnectSuccess(validClient);
                })
                .catch(error => {
                    client.endBusy();
                    this.destroyVisualization().then(() => {
                        this.status =
                            "Erro ao iniciar a conexão com o servidor.";
                    });
                    console.error(error);
                });
        },
        async onConnectSuccess(client) {
            this.client = client;
            this.status = "Conectado com sucesso.";

            await this.$nextTick();
            await sleep(1000);

            const session = this.client.getConnection().getSession();
            connectImageStream(session);
            this.$refs.vtkView.connect(session);
            await this.initialize(this.initialColorBy);
            await this.resetCamera();
            this.starting = false;
            this.connected = true;
            this.status = "Exibindo.";
            await this.$nextTick();
            this.$refs.vtkView.view.resize();
        },
        disconnect() {
            const { client } = this;
            if (client && client.isConnected()) {
                client.disconnect();
            }
        },
        async initialize(initialColorBy) {
            const { client } = this;

            this.status = "Carregando visualização.";
            await this.$nextTick();
            await sleep(1000);

            if (client) {
                this.vtkItems = await Promise.all(
                    this.vtkFiles.map(({ relativePath }) =>
                        client
                            .getRemote()
                            .SwxVisualizer.loadFile(
                                relativePath,
                                initialColorBy
                            )
                    )
                );
            }
        },
        async resetCamera() {
            const { client } = this;

            if (client) {
                await client.getRemote().SwxVisualizer.resetCamera();
            }
        },
        async deleteProxies() {
            const { client, vtkItems } = this;

            if (client && vtkItems.length > 0) {
                await Promise.all(
                    vtkItems.map(({ readerId }) =>
                        client.getRemote().SwxVisualizer.deleteProxy(readerId)
                    )
                );
            }
            this.vtkItems = [];
        },
        async changeVisibility({ index, isVisible }) {
            const item = this.vtkItems[index];
            item.isVisible = isVisible;

            if (this.client && item) {
                await this.client
                    .getRemote()
                    .SwxVisualizer.updateVisibility(
                        item.representationId,
                        item.isVisible
                    );

                this.$set(this.vtkItems, index, item);

                await this.$nextTick();
                await this.resetCamera();
            }
        },
        async changeRepresentation(index, representation) {
            const { client } = this;
            const item = this.vtkItems[index];
            if (client) {
                await this.client
                    .getRemote()
                    .SwxVisualizer.updateRepresentation(
                        item.representationId,
                        representation
                    );
            }
            item.representation = representation;
            this.$set(this.vtkItems, index, item);
            await this.$nextTick();
        },
        async changeColorBy({
            index,
            location,
            name,
            componentName,
            vectorMode,
            component
        }) {
            const { client } = this;
            const item = this.vtkItems[index];
            const { colorBy, readerId } = item;
            if (client) {
                await this.client
                    .getRemote()
                    .SwxVisualizer.updateColorBy(
                        readerId,
                        location,
                        name,
                        componentName,
                        vectorMode,
                        component
                    );
                colorBy.location = location;
                colorBy.name = name;
                colorBy.componentName = componentName;
                colorBy.vectorMode = vectorMode;
                colorBy.component = component;
                item.colorBy = colorBy;

                this.$set(this.vtkItems, index, item);
            }
        },
        async revertTransferFunctions(vtkItem) {
            const { client } = this;

            if (client) {
                await this.client
                    .getRemote()
                    .SwxVisualizer.invertTransferFunctions(
                        vtkItem.representationId
                    );
            }
        }
    }
};
</script>
